import { Matrix4, Matrix4Tuple, Vector3 } from 'three';
import { ExifData, ExifParserFactory } from 'ts-exif-parser';

import { isDefined } from './variables';

/**
 * Parses the first 128kB of a JPEG-File and extracts the DateTimeOriginal from the EXIF data.
 * This takes about 5 to 10 milliseconds and is quicker than reading the entire file.
 */
export function getJpegExifData(file: File): Promise<ExifData> {
  return new Promise((resolve, reject) => {
    // Check if the file type is JPEG, as EXIF data is primarily for JPEG images
    if (file.type !== 'image/jpeg') {
      reject(new Error('File is not a JPEG image.'));
      return;
    }

    // Only read the first 128kB of the file which will be sufficient for reading the EXIF data.
    const slice = file.slice(0, 128 * 1024);
    const reader = new FileReader();

    reader.onload = function (event) {
      const buffer = event.target!.result as ArrayBuffer;
      try {
        resolve(ExifParserFactory.create(buffer).parse());
      } catch (error) {
        reject(new Error('Failed to parse EXIF data.'));
      }
    };

    reader.onerror = function () {
      reject(new Error('Failed to read the file.'));
    };

    reader.readAsArrayBuffer(slice);
  });
}

export function jpegExifDataHasGpsInformation(data: ExifData) {
  return (
    isDefined(data.tags?.GPSAltitude) &&
    isDefined(data.tags?.GPSLatitude) &&
    isDefined(data.tags?.GPSLongitude)
  );
}

/**
 * Gets a basic transformation matrix pointing downwards with the WGS 84 position as
 * its x y z position component.
 */
export function jpegExifExtractCameraTransform(data: ExifData): Matrix4Tuple {
  if (!jpegExifDataHasGpsInformation(data)) {
    throw new Error('No GPS information available in EXIF data.');
  }

  const lat = data.tags?.GPSLatitude!;
  const lon = data.tags?.GPSLongitude!;
  const alt = data.tags?.GPSAltitude!;

  return new Matrix4().setPosition(new Vector3(lon, lat, alt)).toArray();
}
